import { computed, type Signal } from "@angular/core";
import { LvfAppModule } from "@kiraah-shared/common";
import type { MobileModuleAccessService } from "../../profile/services/mobile-module-access.service";
import type { MobileModuleAccessFlags } from "../../profile/helpers/mobile-module-access.models";
import { mergeMobileModuleAccessFlags } from "../../profile/helpers/mobile-module-access.models";

type MobileEquipmentDocumentAccessFlags = Pick<
  MobileModuleAccessFlags,
  "hasUploadAccess" | "hasDownloadAccess" | "hasReadDocumentAccess"
>;

/**
 * Mapping droits UI E-Equipment mobile — aligné wizard profil / seed (`GetEEquipementMobileMenuDefinitions`).
 *
 * | Zone UI        | Module profil                              | Module web (legacy / API)   |
 * |----------------|--------------------------------------------|-----------------------------|
 * | Équipements    | `EEQUIPEMENT_MOBILE_VEHICLE`               | `EQUIPMENT`                 |
 * | Maintenances   | `EEQUIPEMENT_MOBILE_MAINTENANCE`           | `EQUIPMENT_MAINTENANCE`     |
 * | Affectations   | `EEQUIPEMENT_MOBILE_VEHICLE_ASSIGNMENT`    | `EQUIPMENT_ASSIGNMENT`      |
 * | Kilométrages   | `EEQUIPEMENT_MOBILE_KILOMETER`             | `EQUIPMENT_USAGE_METRIC`    |
 * | Contraventions | `EEQUIPEMENT_MOBILE_DRIVER_TRAFFIC_TICKET`  | `DRIVER_TRAFFIC_TICKET`     |
 * | Conducteurs    | `EEQUIPEMENT_MOBILE_DRIVER`                  | `DRIVER`                    |
 */
export interface MobileEquipmentModulePair {
  readonly profileModule: LvfAppModule;
  readonly webModule: LvfAppModule;
}
export type MobileEquipementModulePair = MobileEquipmentModulePair;

export const MOBILE_EQUIPMENT_VEHICLE_MODULES: MobileEquipmentModulePair = {
  profileModule: LvfAppModule.EEQUIPEMENT_MOBILE_VEHICLE,
  webModule: LvfAppModule.EQUIPMENT,
};

export const MOBILE_EQUIPMENT_MAINTENANCE_MODULES: MobileEquipmentModulePair = {
  profileModule: LvfAppModule.EEQUIPEMENT_MOBILE_MAINTENANCE,
  webModule: LvfAppModule.EQUIPMENT_MAINTENANCE,
};

export const MOBILE_EQUIPMENT_ASSIGNMENT_MODULES: MobileEquipmentModulePair = {
  profileModule: LvfAppModule.EEQUIPEMENT_MOBILE_VEHICLE_ASSIGNMENT,
  webModule: LvfAppModule.EQUIPMENT_ASSIGNMENT,
};

export const MOBILE_EQUIPMENT_KILOMETER_MODULES: MobileEquipmentModulePair = {
  profileModule: LvfAppModule.EEQUIPEMENT_MOBILE_KILOMETER,
  webModule: LvfAppModule.EQUIPMENT_USAGE_METRIC,
};

export const MOBILE_EQUIPMENT_CONTRAVENTION_MODULES: MobileEquipmentModulePair = {
  profileModule: LvfAppModule.EEQUIPEMENT_MOBILE_DRIVER_TRAFFIC_TICKET,
  webModule: LvfAppModule.DRIVER_TRAFFIC_TICKET,
};

export const MOBILE_EQUIPMENT_DRIVER_MODULES: MobileEquipmentModulePair = {
  profileModule: LvfAppModule.EEQUIPEMENT_MOBILE_DRIVER,
  webModule: LvfAppModule.DRIVER,
};

export interface MobileEquipmentAppAccess {
  readonly equipmentAccess: Signal<MobileModuleAccessFlags>;
  readonly equipmentDocumentAccess: Signal<MobileEquipmentDocumentAccessFlags>;
  readonly maintenanceAccess: Signal<MobileModuleAccessFlags>;
  readonly assignmentAccess: Signal<MobileModuleAccessFlags>;
  readonly kilometerAccess: Signal<MobileModuleAccessFlags>;
  readonly contraventionAccess: Signal<MobileModuleAccessFlags>;
  readonly driverAccess: Signal<MobileModuleAccessFlags>;
  readonly maintenanceDocumentAccess: Signal<MobileEquipmentDocumentAccessFlags>;
  readonly assignmentDocumentAccess: Signal<MobileEquipmentDocumentAccessFlags>;
  readonly kilometerDocumentAccess: Signal<MobileEquipmentDocumentAccessFlags>;
  readonly contraventionDocumentAccess: Signal<MobileEquipmentDocumentAccessFlags>;
  readonly driverDocumentAccess: Signal<MobileEquipmentDocumentAccessFlags>;
}

function createMergedModuleAccess(
  moduleAccess: MobileModuleAccessService,
  pair: MobileEquipmentModulePair
): Signal<MobileModuleAccessFlags> {
  const webAccess = moduleAccess.moduleAccess(pair.webModule);
  const profileAccess = moduleAccess.moduleAccess(pair.profileModule);
  return computed(() => mergeMobileModuleAccessFlags(webAccess(), profileAccess()));
}

function createMergedDocumentAccess(
  mergedAccess: Signal<MobileModuleAccessFlags>
): Signal<MobileEquipmentDocumentAccessFlags> {
  return computed(() => {
    const flags = mergedAccess();
    return {
      hasUploadAccess: flags.hasUploadAccess,
      hasDownloadAccess: flags.hasDownloadAccess,
      hasReadDocumentAccess: flags.hasReadDocumentAccess,
    };
  });
}

export function createMobileEquipmentAppAccess(
  moduleAccess: MobileModuleAccessService
): MobileEquipmentAppAccess {
  const equipmentAccess = createMergedModuleAccess(moduleAccess, MOBILE_EQUIPMENT_VEHICLE_MODULES);
  const maintenanceAccess = createMergedModuleAccess(moduleAccess, MOBILE_EQUIPMENT_MAINTENANCE_MODULES);
  const assignmentAccess = createMergedModuleAccess(moduleAccess, MOBILE_EQUIPMENT_ASSIGNMENT_MODULES);
  const kilometerAccess = createMergedModuleAccess(moduleAccess, MOBILE_EQUIPMENT_KILOMETER_MODULES);
  const contraventionAccess = createMergedModuleAccess(moduleAccess, MOBILE_EQUIPMENT_CONTRAVENTION_MODULES);
  const driverAccess = createMergedModuleAccess(moduleAccess, MOBILE_EQUIPMENT_DRIVER_MODULES);
  const equipmentDocumentAccess = createMergedDocumentAccess(equipmentAccess);

  return {
    equipmentAccess,
    equipmentDocumentAccess,
    maintenanceAccess,
    assignmentAccess,
    kilometerAccess,
    contraventionAccess,
    driverAccess,
    maintenanceDocumentAccess: createMergedDocumentAccess(maintenanceAccess),
    assignmentDocumentAccess: createMergedDocumentAccess(assignmentAccess),
    kilometerDocumentAccess: createMergedDocumentAccess(kilometerAccess),
    contraventionDocumentAccess: createMergedDocumentAccess(contraventionAccess),
    driverDocumentAccess: createMergedDocumentAccess(driverAccess),
  };
}

/** Droits documents associés selon le module web de l'entité (filtre API inchangé). */
export function resolveMobileEquipmentDocumentAccess(
  equipmentAccess: MobileEquipmentAppAccess,
  webDocumentModule: LvfAppModule
): MobileEquipmentDocumentAccessFlags {
  switch (webDocumentModule) {
    case LvfAppModule.EQUIPMENT:
      return equipmentAccess.equipmentDocumentAccess();
    case LvfAppModule.EQUIPMENT_MAINTENANCE:
      return equipmentAccess.maintenanceDocumentAccess();
    case LvfAppModule.EQUIPMENT_ASSIGNMENT:
      return equipmentAccess.assignmentDocumentAccess();
    case LvfAppModule.EQUIPMENT_USAGE_METRIC:
      return equipmentAccess.kilometerDocumentAccess();
    case LvfAppModule.DRIVER_TRAFFIC_TICKET:
      return equipmentAccess.contraventionDocumentAccess();
    case LvfAppModule.DRIVER:
      return equipmentAccess.driverDocumentAccess();
    default:
      return {
        hasUploadAccess: false,
        hasDownloadAccess: false,
        hasReadDocumentAccess: false,
      };
  }
}
